import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { LuChevronsLeft, LuLayoutGrid, LuLogOut, LuX } from 'react-icons/lu';
import { usePendingLeaves } from '../contexts/PendingLeavesContext';
import useSignOut from '../hooks/useSignOut';
import useMediaQuery from '../hooks/useMediaQuery';
import logoMark from '../assets/logo_min.png';
import { GLASS_CHIP, GLASS_PRESSABLE } from './ui/glass';
import { menuItems } from './shell/menuConfig';
import useVisibleMenuItems from './shell/useVisibleMenuItems';
import SidebarItem from './shell/SidebarItem';
import { useTranslation } from '../i18n/LanguageContext';

// The menu tree and access lists used to live here; keep the old import path.
export { menuItems, PAGE_ACCESS_VIEWERS, canViewPageAccess, SPECIAL_EMAIL_ACCESS } from './shell/menuConfig';

const footerButtonClass = (compact) => [
  'tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-2xl tw-p-1.5 tw-text-sm tw-font-medium tw-bg-transparent',
  'tw-transition-colors tw-duration-200 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50',
  compact ? 'tw-justify-center' : 'tw-pr-2.5',
].join(' ');

const isActiveRoute = (pathname, item) =>
  pathname === item.path || item.submenu.some((sub) => sub.path === pathname);

/**
 * Portal navigation. Desktop (md+): a floating glass panel that can collapse
 * to icons (`collapsed`, owned and persisted by Layout). Mobile: a drawer that
 * slides in from the left while `mobileOpen`.
 */
const SideBar = ({ collapsed, onToggleCollapse, mobileOpen, onCloseMobile }) => {
  const { pathname } = useLocation();
  const { t, language } = useTranslation();
  const { pendingLeaveCount } = usePendingLeaves();
  const { signOutUser, isSigningOut } = useSignOut();
  const visibleMenuItems = useVisibleMenuItems();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const compact = collapsed && isDesktop;
  const [expandedMenus, setExpandedMenus] = useState({});

  // One group open at a time
  const toggleMenu = useCallback((title) => {
    setExpandedMenus((prev) => ({ [title]: !prev[title] }));
  }, []);

  // When the route changes, open the group that contains it
  useEffect(() => {
    menuItems.forEach((item) => {
      if (item.submenu.some((sub) => sub.path === pathname)) {
        setExpandedMenus((prev) => ({ ...prev, [item.title]: true }));
      }
    });
  }, [pathname]);

  // A group clicked while collapsed: widen the panel and open that group
  const handleToggle = useCallback((title) => {
    if (compact) {
      onToggleCollapse();
      setExpandedMenus({ [title]: true });
      return;
    }
    toggleMenu(title);
  }, [compact, onToggleCollapse, toggleMenu]);

  return (
    <>
      {mobileOpen && (
        <div
          aria-hidden="true"
          onClick={onCloseMobile}
          className="tw-fixed tw-inset-0 tw-z-[1039] tw-bg-slate-950/45 md:tw-hidden"
        />
      )}

      <aside
        lang={language}
        aria-label={t('shell.mainMenu')}
        className={[
          'tw-fixed tw-inset-y-0 tw-left-0 tw-z-[1040] tw-flex tw-w-[17rem] tw-max-w-[85vw] tw-flex-col tw-font-sans tw-antialiased',
          'tw-rounded-r-[1.75rem] tw-border tw-border-white/70 tw-bg-white/95 tw-text-slate-700',
          'tw-bg-[linear-gradient(180deg,rgba(255,255,255,0.9),rgba(248,250,252,0.6))]',
          'tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_24px_60px_-24px_rgba(15,23,42,0.35)]',
          'dark:tw-border-white/10 dark:tw-bg-[#22262f]/95 dark:tw-text-slate-200',
          'dark:tw-bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0))]',
          'dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_24px_60px_-24px_rgba(0,0,0,0.9)]',
          // visibility: a closed drawer must not stay reachable with Tab
          'tw-transition-[transform,width,visibility] tw-duration-300 tw-ease-out',
          mobileOpen ? 'tw-translate-x-0' : '-tw-translate-x-[110%] max-md:tw-invisible',
          'md:tw-inset-y-3 md:tw-left-3 md:tw-z-[999] md:tw-max-w-none md:tw-translate-x-0 md:tw-rounded-[1.75rem]',
          compact ? 'md:tw-w-[4.75rem]' : 'md:tw-w-[16.5rem]',
        ].join(' ')}
      >
        {/* Desktop collapse handle on the panel's edge */}
        <button
          type="button"
          onClick={onToggleCollapse}
          aria-label={compact ? t('shell.expandMenu') : t('shell.collapseMenu')}
          title={compact ? t('shell.expandMenu') : t('shell.collapseMenu')}
          className={`tw-absolute -tw-right-3 tw-top-7 tw-z-10 tw-hidden tw-h-7 tw-w-7 tw-place-items-center tw-rounded-full tw-text-slate-500 hover:tw-text-slate-900 md:tw-grid dark:tw-text-slate-300 dark:hover:tw-text-white ${GLASS_CHIP} ${GLASS_PRESSABLE}`}
        >
          <LuChevronsLeft
            aria-hidden="true"
            className={`tw-h-4 tw-w-4 tw-transition-transform tw-duration-300 ${compact ? 'tw-rotate-180' : ''}`}
          />
        </button>

        <div className={`tw-flex tw-h-20 tw-shrink-0 tw-items-center tw-gap-3 ${compact ? 'tw-justify-center tw-px-2' : 'tw-px-5'}`}>
          <img src={logoMark} alt="HabcareCRO" className="tw-h-10 tw-w-auto tw-shrink-0" />
          {!compact && (
            <div className="tw-min-w-0 tw-flex-1 tw-leading-tight">
              <p className="tw-truncate tw-text-[15px] tw-font-semibold tw-tracking-tight tw-text-slate-900 dark:tw-text-white">HabcareCRO</p>
              <p className="tw-font-mono tw-text-[10px] tw-uppercase tw-tracking-[0.3em] tw-text-slate-500 dark:tw-text-slate-400">{t('shell.portal')}</p>
            </div>
          )}
          <button
            type="button"
            onClick={onCloseMobile}
            aria-label={t('shell.closeMenu')}
            className={`tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-full tw-text-slate-500 md:tw-hidden dark:tw-text-slate-300 ${GLASS_CHIP} ${GLASS_PRESSABLE}`}
          >
            <LuX aria-hidden="true" className="tw-h-4 tw-w-4" />
          </button>
        </div>

        <nav className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-overflow-x-hidden tw-px-3 tw-pb-3 glass-scrollbar">
          <ul className="tw-space-y-1">
            {visibleMenuItems.map((item) => (
              <SidebarItem
                key={item.path}
                item={item}
                pathname={pathname}
                isActive={isActiveRoute(pathname, item)}
                expanded={Boolean(expandedMenus[item.title])}
                compact={compact}
                pendingLeaveCount={pendingLeaveCount}
                onToggle={handleToggle}
                onNavigate={onCloseMobile}
              />
            ))}
          </ul>
        </nav>

        <div className="tw-shrink-0 tw-space-y-1 tw-border-t tw-border-slate-200/70 tw-p-3 dark:tw-border-white/10">
          <Link
            to="/hall"
            title={compact ? t('shell.hall') : undefined}
            className={`${footerButtonClass(compact)} tw-text-slate-600 hover:tw-bg-slate-900/[0.04] hover:tw-text-slate-900 dark:tw-text-slate-300 dark:hover:tw-bg-white/[0.06] dark:hover:tw-text-white`}
          >
            <span className={`tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-xl ${GLASS_CHIP}`}>
              <LuLayoutGrid aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
            </span>
            {!compact && <span className="tw-truncate">{t('shell.hall')}</span>}
          </Link>
          <button
            type="button"
            onClick={signOutUser}
            disabled={isSigningOut}
            title={compact ? t('shell.signOut') : undefined}
            className={`${footerButtonClass(compact)} tw-text-rose-600 hover:tw-bg-rose-500/10 disabled:tw-opacity-60 dark:tw-text-rose-300 dark:hover:tw-bg-rose-400/10`}
          >
            <span className={`tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-xl ${GLASS_CHIP}`}>
              <LuLogOut aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
            </span>
            {!compact && <span className="tw-truncate">{isSigningOut ? t('shell.signingOut') : t('shell.signOut')}</span>}
          </button>
        </div>
      </aside>
    </>
  );
};

SideBar.propTypes = {
  collapsed: PropTypes.bool,
  onToggleCollapse: PropTypes.func.isRequired,
  mobileOpen: PropTypes.bool,
  onCloseMobile: PropTypes.func.isRequired,
};

export default SideBar;
